import React, { useContext, useEffect, useState } from 'react';
import {
    Avatar,
    Backdrop,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Grid2 as Grid,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import {
    AccountBalanceWalletRounded,
    BusinessRounded,
    CheckCircleRounded,
    EditRounded,
    SwapHorizRounded,
} from "@mui/icons-material";
import { connect, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { corporateActions } from "../../helper/redux/actions/CorporateActions";
import { ErrorContext } from "../../components/GympinPagesProvider";
import { corporatePersonnel_corporateOwnedByUserId } from "../../network/api/corporatePersonnel.api";
import { CorporateContractType } from "../../helper/enums/CorporateContractType";
import { CorporateStatusType } from "../../helper/enums/CorporateStatusType";
import { toPriceWithComma } from "../../helper/utils";
import store from "../../helper/redux/store";
import { sagaActions } from "../../helper/redux/actions/SagaActions";

const SettingsCorporate = (props) => {
    const theme = useTheme();
    const error = useContext(ErrorContext);
    const navigate = useNavigate();
    const user = useSelector(({ auth }) => auth.user);
    const { corporate } = useSelector(({ corporate }) => corporate);

    const [selectedCorporate, setSelectedCorporate] = useState(corporate);
    const [personCorporates, setPersonCorporates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [switching, setSwitching] = useState(false);

    useEffect(() => {
        document.title = 'سازمان‌های من | جیم‌پین سازمانی';
        getUserCorporates();
    }, []);

    useEffect(() => {
        if (selectedCorporate?.Id && corporate?.Id && selectedCorporate.Id !== corporate.Id) {
            setSwitching(true);
            store.dispatch(sagaActions.RequestCorporate(selectedCorporate));
            props.SetCorporate(selectedCorporate);
            setTimeout(() => {
                setSwitching(false);
                window.location = "/";
            }, 1500);
        }
    }, [selectedCorporate, corporate]);

    function getUserCorporates() {
        if (!user?.Id) return;
        setLoading(true);
        corporatePersonnel_corporateOwnedByUserId({ id: user.Id })
            .then(result => {
                const list = result?.data?.Data || [];
                setPersonCorporates(list);
                if (list.length === 1 && selectedCorporate?.Id !== list[0]?.Corporate?.Id) {
                    setSelectedCorporate(list[0]?.Corporate);
                    props.SetCorporate(list[0]?.Corporate);
                }
            })
            .catch(e => {
                const msg = e?.response?.data?.Message || "خطا در دریافت لیست سازمان‌ها";
                error.showError({ message: msg });
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function handleSelectCorporate(corpItem) {
        if (!corpItem || corpItem.Id === corporate?.Id) return;
        setSwitching(true);
        setSelectedCorporate(corpItem);
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <title>سازمان‌های من | جیم‌پین سازمانی</title>

            {/* Switching Backdrop */}
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 20,
                    backdropFilter: 'blur(4px)',
                    bgcolor: 'rgba(15, 23, 42, 0.75)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
                open={switching}
            >
                <CircularProgress color="inherit" size={48} thickness={4} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    در حال تغییر فضای سازمانی به «{selectedCorporate?.Name}»...
                </Typography>
            </Backdrop>

            {/* Executive Page Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 3.5,
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 54,
                            height: 54,
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.main',
                            color: '#fff',
                            boxShadow: '0 8px 20px rgba(45, 107, 226, 0.3)',
                        }}
                    >
                        <BusinessRounded sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                            سازمان‌های من
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            مشاهده، انتخاب و ویرایش اطلاعات سازمان‌ها و شرکت‌های تحت مدیریت شما
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Loading Skeleton */}
            {loading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={40} thickness={4} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        در حال بارگذاری لیست سازمان‌ها...
                    </Typography>
                </Box>
            )}

            {/* Empty State */}
            {!loading && personCorporates.length === 0 && (
                <Card
                    elevation={0}
                    sx={{
                        p: 6,
                        borderRadius: 4,
                        textAlign: 'center',
                        border: '1px dashed',
                        borderColor: 'divider',
                    }}
                >
                    <BusinessRounded sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        سازمانی یافت نشد
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        هیچ سازمانی به حساب کاربری شما متصل نیست. در صورت نیاز با پشتیبانی جیم‌پین تماس بگیرید.
                    </Typography>
                </Card>
            )}

            {/* Organization Cards Grid */}
            {!loading && personCorporates.length > 0 && (
                <Grid container spacing={3}>
                    {personCorporates.map((item, index) => {
                        const corp = item?.Corporate;
                        if (!corp) return null;
                        const isCurrentActive = corporate?.Id === corp.Id;
                        const totalDeposit = corp?.FinanceCorporate?.TotalDeposit || 0;

                        return (
                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={corp.Id || index}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 4,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        border: '2px solid',
                                        borderColor: isCurrentActive ? 'primary.main' : 'divider',
                                        bgcolor: isCurrentActive ? 'action.selected' : 'background.paper',
                                        boxShadow: isCurrentActive
                                            ? '0 12px 28px rgba(45, 107, 226, 0.15)'
                                            : '0 4px 16px rgba(0,0,0,0.03)',
                                        transition: 'all 0.25s ease',
                                        '&:hover': {
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
                                        },
                                    }}
                                >
                                    <Box>
                                        {/* Status Top Row */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                            {isCurrentActive ? (
                                                <Chip
                                                    size="small"
                                                    color="primary"
                                                    icon={<CheckCircleRounded sx={{ fontSize: 16 }} />}
                                                    label="سازمان فعال فعلی"
                                                    sx={{ fontWeight: 800, fontSize: '0.75rem', px: 0.5 }}
                                                />
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    شناسه: #{corp.Id}
                                                </Typography>
                                            )}

                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                color={corp.Status === 'ACTIVE' ? 'success' : 'default'}
                                                label={CorporateStatusType[corp.Status] || corp.Status}
                                                sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                                            />
                                        </Box>

                                        {/* Center: Logo & Name */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', my: 2 }}>
                                            <Avatar
                                                src={corp?.Logo?.Url}
                                                alt={corp.Name}
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    mb: 2,
                                                    bgcolor: 'primary.50',
                                                    color: 'primary.main',
                                                    boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                                                    border: '3px solid',
                                                    borderColor: isCurrentActive ? 'primary.light' : 'divider',
                                                }}
                                            >
                                                <BusinessRounded sx={{ fontSize: 40 }} />
                                            </Avatar>

                                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                                                {corp.Name}
                                            </Typography>

                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                نوع قرارداد: {CorporateContractType[corp.ContractType] || corp.ContractType || 'ثبت نشده'}
                                            </Typography>
                                        </Box>

                                        {/* Wallet Balance Preview */}
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 2.5,
                                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                                border: '1px solid',
                                                borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mb: 3,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AccountBalanceWalletRounded color="primary" sx={{ fontSize: 18 }} />
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                    موجودی کیف‌پول:
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                                {toPriceWithComma(totalDeposit)} <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>تومان</span>
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Action Buttons */}
                                    <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                        {isCurrentActive ? (
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<EditRounded />}
                                                onClick={() => navigate('/management/details')}
                                                sx={{
                                                    borderRadius: 2.5,
                                                    py: 1,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                ویرایش اطلاعات سازمان
                                            </Button>
                                        ) : (
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="primary"
                                                startIcon={<SwapHorizRounded />}
                                                onClick={() => handleSelectCorporate(corp)}
                                                sx={{
                                                    borderRadius: 2.5,
                                                    py: 1,
                                                    fontWeight: 700,
                                                    boxShadow: '0 4px 12px rgba(45, 107, 226, 0.25)',
                                                }}
                                            >
                                                انتخاب این سازمان
                                            </Button>
                                        )}
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Container>
    );
};

export default connect(null, corporateActions)(SettingsCorporate);
